import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Pencil } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  useToast,
} from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import type { VehicleHandover } from "@/vehicleHandovers/vehicleHandover";
import {
  getGetAdminVehicleHandoversIdQueryKey,
  useGetAdminVehicleHandoversId,
} from "@/lib/api/generated/vehicle-handover/vehicle-handover";
import { useUpdateVehicleHandover } from "@/vehicleHandovers/useVehicleHandoverActions";
import { VehicleHandoverActions } from "@/vehicleHandovers/VehicleHandoverActions";
import { VehicleHandoverFollowUp } from "@/vehicleHandovers/VehicleHandoverFollowUp";
import { toVehicleHandoverPatchInput } from "@/vehicleHandovers/vehicleHandoverForm";
import {
  VehicleHandoverDetails,
  vehicleHandoverDetailLabel,
  vehicleHandoverVehicleTitle,
} from "@/vehicleHandovers/VehicleHandoverDetails";
import {
  DEFAULT_HANDOVER_STATUS_FILTER,
  handoverStatusLabel,
  handoverStatusTone,
} from "@/vehicleHandovers/vehicleHandoverLabels";
import { VehicleHandoverSheet } from "@/vehicleHandovers/VehicleHandoverSheet";
import { VehicleHandoverSwap } from "@/vehicleHandovers/VehicleHandoverSwap";
import { getAffiliateId } from "@/auth/authStore";

/** The permission the step buttons need; without it the return is read-only. */
const TRANSITION_PERMISSION = "admin_vehicle_handover_transition";

/** The permission needed to correct what was reported. */
const UPDATE_PERMISSION = "admin_vehicle_handover_update";

const SKELETON_KEYS = ["a", "b", "c", "d"];

export const Route = createFileRoute(
  "/_authenticated/vehicle-handovers_/$vehicleHandoverId",
)({
  component: VehicleHandoverDetailPage,
});

function VehicleHandoverDetailPage(): ReactNode {
  const { vehicleHandoverId } = Route.useParams();
  const { data, isPending, isError, refetch } = useGetAdminVehicleHandoversId(
    vehicleHandoverId,
    {
      query: {
        queryKey: [
          ...getGetAdminVehicleHandoversIdQueryKey(vehicleHandoverId),
          getAffiliateId(),
        ],
      },
    },
  );

  return (
    <div className="space-y-4">
      <Link
        to="/vehicle-handovers"
        search={{ page: 1, status: DEFAULT_HANDOVER_STATUS_FILTER }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar innames
      </Link>

      {isError ? (
        <Card>
          <CardContent className="py-10 text-center">
            <div className="space-y-3" role="alert">
              <p className="text-muted-foreground">
                We konden deze inname niet laden.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Opnieuw proberen
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <VehicleHandoverDetailHeader
            vehicleHandover={data}
            isPending={isPending}
          />

          {data?.type === "return" && data.status === "completed" ? (
            <VehicleHandoverFollowUp vehicleHandover={data} />
          ) : data?.type === "return" ? (
            <RequirePermission permission={TRANSITION_PERMISSION}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Vervolgstappen</CardTitle>
                </CardHeader>
                <CardContent>
                  <VehicleHandoverActions
                    vehicleHandoverId={vehicleHandoverId}
                    plannedAt={data?.plannedAt}
                  />
                </CardContent>
              </Card>
            </RequirePermission>
          ) : null}

          {isPending ? (
            <Card>
              <CardContent className="space-y-3 py-6">
                {SKELETON_KEYS.map((key) => (
                  <Skeleton key={key} className="h-4 w-full max-w-md" />
                ))}
              </CardContent>
            </Card>
          ) : (
            <>
              <VehicleHandoverSwap vehicleHandover={data} />
              <VehicleHandoverDetails vehicleHandover={data} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function VehicleHandoverDetailHeader({
  vehicleHandover,
  isPending,
}: {
  readonly vehicleHandover: VehicleHandover | undefined;
  readonly isPending: boolean;
}): ReactNode {
  if (isPending || !vehicleHandover) {
    return <Skeleton className="h-9 w-64" />;
  }

  const title =
    vehicleHandover.vehicleLicensePlate ||
    vehicleHandoverVehicleTitle(vehicleHandover) ||
    vehicleHandoverDetailLabel(vehicleHandover.type);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <Badge variant={handoverStatusTone(vehicleHandover.status)} size="sm">
        {handoverStatusLabel(vehicleHandover.status)}
      </Badge>
      {vehicleHandover.type === "return" ? (
        <div className="ml-auto">
          <VehicleHandoverEditAction vehicleHandover={vehicleHandover} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Correcting what was reported. Kept apart from the step buttons because it
 * changes what the hand-in is, not how far along it is - and because a reason
 * or a destination entered in haste has to stay correctable while the return
 * is being worked on.
 */
function VehicleHandoverEditAction({
  vehicleHandover,
}: {
  readonly vehicleHandover: VehicleHandover;
}): ReactNode {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const updateMutation = useUpdateVehicleHandover(vehicleHandover.id);

  return (
    <RequirePermission permission={UPDATE_PERMISSION}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Pencil className="size-4" aria-hidden="true" />
        Bewerken
      </Button>
      <VehicleHandoverSheet
        open={open}
        onOpenChange={setOpen}
        vehicleHandover={vehicleHandover}
        isSubmitting={updateMutation.isPending}
        onSubmit={(values) =>
          updateMutation.mutate(toVehicleHandoverPatchInput(values), {
            onSuccess: () => {
              setOpen(false);
              toast.success("Inname bijgewerkt.");
            },
          })
        }
      />
    </RequirePermission>
  );
}
